/**
 * Admin Orca Repo RPC definitions
 *
 * Operator-facing CRUD surface over the `orca_repos` registry — one row per
 * repository orca can build against, holding the operator-tunable per-repo
 * configuration (mode, merge policy, worker image pins, secrets reference,
 * contract override, team-key routing defaults). The admin portal's
 * Repositories pages bind to this group.
 *
 * Operator-only: registry rows control merge behavior, secrets references, and
 * container env, so every method carries {@link AdminMiddleware} — there is no
 * actor-scoped variant.
 *
 * @since 1.0.0
 */
import { Rpc, RpcGroup } from "@effect/rpc";
import { OrcaRepo, OrcaRepoAgentCli, OrcaRepoMergeMethod, OrcaRepoMode, OrcaRepoSeen } from "@fm/db/models/OrcaRepo";
import { Schema } from "effect";
import { REPO_NAME_PATTERN } from "../../services/orca/repo-from-labels.ts";
import { AdminMiddleware } from "../admin.tags.ts";
import { DatabaseError, Forbidden, RecordNotFoundError, Unauthorized } from "../errors.ts";

// ============================================================================
// Request Schemas
// ============================================================================

/**
 * Reference to a single registry row by its canonical `owner/name` slug — the
 * input for {@link AdminOrcaRepoRpcs get} and {@link AdminOrcaRepoRpcs delete}.
 *
 * @since 1.0.0
 */
export const OrcaRepoRef = Schema.Struct({
  repo: Schema.String,
});

/**
 * @since 1.0.0
 */
export type OrcaRepoRef = Schema.Schema.Type<typeof OrcaRepoRef>;

/**
 * Full editable column set for an upsert — everything on {@link OrcaRepo}
 * except the generated `id` and the `created_at`/`updated_at` timestamps.
 *
 * Validation at the wire boundary:
 *  - `repo` must be a GitHub `owner/name` slug (mirrors {@link REPO_NAME_PATTERN}
 *    from repo-from-labels — the same shape a `repo:` Linear label carries).
 *  - `mode` / `mergeMethod` / `defaultAgentCli` are constrained to their literal
 *    unions ({@link OrcaRepoMode} / {@link OrcaRepoMergeMethod} /
 *    {@link OrcaRepoAgentCli}) so a typo'd vocabulary value is rejected here, not
 *    silently persisted.
 *
 * The `env` column is NON-SECRET by contract: it must hold only non-sensitive
 * values injected into builder containers. Secrets flow via `secretsRepo`, never
 * here — the upsert handler additionally logs the written env key names on every
 * write as an operator audit trail.
 *
 * @since 1.0.0
 */
export const OrcaRepoUpsertInput = Schema.Struct({
  repo: Schema.String.pipe(Schema.pattern(REPO_NAME_PATTERN)),
  mode: OrcaRepoMode,
  defaultBranch: Schema.NullOr(Schema.String),
  mergeMethod: OrcaRepoMergeMethod,
  deleteBranch: Schema.Boolean,
  contract: Schema.NullOr(Schema.Record({ key: Schema.String, value: Schema.Unknown })),
  secretsRepo: Schema.NullOr(Schema.String),
  env: Schema.NullOr(Schema.Record({ key: Schema.String, value: Schema.String })),
  defaultModel: Schema.NullOr(Schema.String),
  defaultAgentCli: Schema.NullOr(OrcaRepoAgentCli),
  maxAttempts: Schema.NullOr(Schema.Number),
  maxParallel: Schema.NullOr(Schema.Number),
  workerImage: Schema.NullOr(Schema.String),
  workerTag: Schema.NullOr(Schema.String),
  workerMemoryLimit: Schema.NullOr(Schema.String),
  teamKeys: Schema.NullOr(Schema.Array(Schema.String)),
  // Mirror-mode pairing (mode === "mirror"): the client repo mirrored, the
  // branch fetched from it, and the mirror branch fast-forwarded. NULL for
  // direct/default repos. Required-with-explicit-null like every other column
  // (no partial patch).
  upstream: Schema.NullOr(Schema.String),
  upstreamBranch: Schema.NullOr(Schema.String),
  mirrorBranch: Schema.NullOr(Schema.String),
  notes: Schema.NullOr(Schema.String),
  // OL-61: the customer this repo belongs to. This is an operator ASSIGNMENT —
  // it decides who owns the repo (and therefore who owns every task, project
  // and usage row that inherits its owner from the repo). It is NOT a scope
  // field: it never decides whose rows the caller may see, and it is safe only
  // because this whole group is admin-only (`AdminMiddleware` requires the
  // effective `admin` role on both auth paths), so every caller is an
  // Orcaloops operator.
  //
  // Required-with-explicit-null like every other column here (no partial
  // patch); a `null` means "inherit the internal org", resolved server-side.
  customerId: Schema.NullOr(Schema.String),
});

/**
 * @since 1.0.0
 */
export type OrcaRepoUpsertInput = Schema.Schema.Type<typeof OrcaRepoUpsertInput>;

// ============================================================================
// Seen-repos read model
// ============================================================================

/**
 * Wire schema for the seen-repos read model — the union of every registered
 * repo and every repo orca has built against. Re-exports the {@link OrcaRepoSeen}
 * db read model (the single source of truth) so the wire shape can never drift
 * from the accessor's return type.
 *
 * @since 1.0.0
 */
export const OrcaRepoSeenRow = OrcaRepoSeen;

/**
 * @since 1.0.0
 */
export type OrcaRepoSeenRow = Schema.Schema.Type<typeof OrcaRepoSeenRow>;

// ============================================================================
// Preflight schemas
// ============================================================================

/**
 * Identifier of one preflight health check.
 *
 * @since 1.0.0
 */
export const OrcaRepoPreflightCheckId = Schema.Literal(
  "app",
  "access",
  "ruleset",
  "contract",
  "expectedChecks",
  "linearLabel",
  "mirrorSync",
);

/**
 * @since 1.0.0
 */
export type OrcaRepoPreflightCheckId = Schema.Schema.Type<typeof OrcaRepoPreflightCheckId>;

/**
 * Outcome of one preflight check. `unknown` means the probe could not answer
 * (a credentialed call failed / a CLI errored) — never a raw error on the wire.
 *
 * @since 1.0.0
 */
export const OrcaRepoPreflightStatus = Schema.Literal("pass", "fail", "warn", "unknown");

/**
 * @since 1.0.0
 */
export type OrcaRepoPreflightStatus = Schema.Schema.Type<typeof OrcaRepoPreflightStatus>;

/**
 * A single preflight check result. `result` is a human-readable one-liner;
 * `fix` is the ordered remediation steps for a failing check (empty otherwise).
 *
 * @since 1.0.0
 */
export const OrcaRepoPreflightCheck = Schema.Struct({
  id: OrcaRepoPreflightCheckId,
  status: OrcaRepoPreflightStatus,
  name: Schema.String,
  result: Schema.String,
  fix: Schema.Array(Schema.String),
});

/**
 * @since 1.0.0
 */
export type OrcaRepoPreflightCheck = Schema.Schema.Type<typeof OrcaRepoPreflightCheck>;

/**
 * The seven preflight checks for a repo as data — never a thrown error. Each
 * failing probe is reported with its fix steps.
 *
 * @since 1.0.0
 */
export const OrcaRepoPreflight = Schema.Struct({
  repo: Schema.String,
  checkedAt: Schema.DateTimeUtc,
  checks: Schema.Array(OrcaRepoPreflightCheck),
});

/**
 * @since 1.0.0
 */
export type OrcaRepoPreflight = Schema.Schema.Type<typeof OrcaRepoPreflight>;

// ============================================================================
// Resolved-contract schemas
// ============================================================================

/**
 * Provenance of a resolved contract field, in the worker's resolution order.
 *
 * @since 1.0.0
 */
export const OrcaContractSource = Schema.Literal("in-repo", "registry", "baked", "default");

/**
 * @since 1.0.0
 */
export type OrcaContractSource = Schema.Schema.Type<typeof OrcaContractSource>;

/**
 * One resolved contract field: its name, the winning value, and which source
 * supplied it.
 *
 * @since 1.0.0
 */
export const OrcaResolvedContractField = Schema.Struct({
  name: Schema.String,
  value: Schema.Unknown,
  source: OrcaContractSource,
});

/**
 * @since 1.0.0
 */
export type OrcaResolvedContractField = Schema.Schema.Type<typeof OrcaResolvedContractField>;

/**
 * Per-field provenance of a repo's effective contract, resolved read-side in
 * the worker's order (in-repo `orca.json` → registry `contract` → baked
 * `/contracts/<owner>__<name>.json` → fm defaults). Every known contract field
 * appears; an absent field is included with source `default` and the fm default
 * (or null). The raw sources are echoed so the wizard can show them.
 *
 * @since 1.0.0
 */
export const OrcaRepoResolvedContract = Schema.Struct({
  repo: Schema.String,
  fields: Schema.Array(OrcaResolvedContractField),
  inRepoText: Schema.NullOr(Schema.String),
  inRepoMalformed: Schema.Boolean,
  registry: Schema.NullOr(Schema.Record({ key: Schema.String, value: Schema.Unknown })),
  bakedPath: Schema.NullOr(Schema.String),
});

/**
 * @since 1.0.0
 */
export type OrcaRepoResolvedContract = Schema.Schema.Type<typeof OrcaRepoResolvedContract>;

// ============================================================================
// File-read schemas
// ============================================================================

/**
 * The only two files the wizard may read from a repo — restricted by literal
 * union so a `file` call can never be turned into an arbitrary-path read.
 *
 * @since 1.0.0
 */
export const OrcaRepoFilePath = Schema.Literal("orca.json", "package.json");

/**
 * @since 1.0.0
 */
export type OrcaRepoFilePath = Schema.Schema.Type<typeof OrcaRepoFilePath>;

/**
 * @since 1.0.0
 */
export const OrcaRepoFileInput = Schema.Struct({
  repo: Schema.String,
  path: OrcaRepoFilePath,
});

/**
 * @since 1.0.0
 */
export type OrcaRepoFileInput = Schema.Schema.Type<typeof OrcaRepoFileInput>;

/**
 * A repo file's raw contents, or `text: null` when the file is absent (404) or
 * a GitHub auth failure prevented the read (surfaced as a warning log, never a
 * wire error).
 *
 * @since 1.0.0
 */
export const OrcaRepoFile = Schema.Struct({
  repo: Schema.String,
  path: OrcaRepoFilePath,
  text: Schema.NullOr(Schema.String),
});

/**
 * @since 1.0.0
 */
export type OrcaRepoFile = Schema.Schema.Type<typeof OrcaRepoFile>;

// ============================================================================
// Onboard schemas
// ============================================================================

/**
 * Input for the onboarding wizard's final step: the registry upsert plus an
 * optional Linear team key on which to ensure the `repo:<slug>` label exists.
 *
 * @since 1.0.0
 */
export const OrcaRepoOnboardInput = Schema.Struct({
  upsert: OrcaRepoUpsertInput,
  linearTeamKey: Schema.NullOr(Schema.String),
});

/**
 * @since 1.0.0
 */
export type OrcaRepoOnboardInput = Schema.Schema.Type<typeof OrcaRepoOnboardInput>;

/**
 * One onboarding step result. A step never fails the RPC — a Linear failure is
 * reported as `ok: false` so the wizard can retry that step alone.
 *
 * @since 1.0.0
 */
export const OrcaRepoOnboardStep = Schema.Struct({
  id: Schema.Literal("registry", "linearLabel"),
  ok: Schema.Boolean,
  detail: Schema.String,
});

/**
 * @since 1.0.0
 */
export type OrcaRepoOnboardStep = Schema.Schema.Type<typeof OrcaRepoOnboardStep>;

/**
 * Per-step result of an onboarding run. `row` is the upserted registry row (or
 * null if the upsert itself never happened).
 *
 * @since 1.0.0
 */
export const OrcaRepoOnboardResult = Schema.Struct({
  repo: Schema.String,
  row: Schema.NullOr(OrcaRepo.json),
  steps: Schema.Array(OrcaRepoOnboardStep),
});

/**
 * @since 1.0.0
 */
export type OrcaRepoOnboardResult = Schema.Schema.Type<typeof OrcaRepoOnboardResult>;

// ============================================================================
// RPC Group
// ============================================================================

/**
 * Admin Orca Repo RPC group — list/get/upsert/delete over the `orca_repos`
 * registry.
 *
 * All endpoints require admin role via {@link AdminMiddleware}. This is an
 * operator-only surface: the rows control merge behavior, secrets references,
 * and container env, so there is no actor-scoped variant.
 *
 * @since 1.0.0
 */
export class AdminOrcaRepoRpcs extends RpcGroup.make(
  // Every registered repo row, ordered by slug. Read-only registry listing for
  // the admin Repositories page.
  Rpc.make("admin.orcaRepo.list", {
    success: Schema.Array(OrcaRepo.json),
    error: Schema.Union(Unauthorized, Forbidden, DatabaseError),
  }).middleware(AdminMiddleware),
  // Resolve one row by its `owner/name` slug. A slug that matches no row yields
  // RecordNotFoundError.
  Rpc.make("admin.orcaRepo.get", {
    payload: OrcaRepoRef,
    success: OrcaRepo.json,
    error: Schema.Union(Unauthorized, Forbidden, RecordNotFoundError, DatabaseError),
  }).middleware(AdminMiddleware),
  // Idempotent insert-or-update keyed on the `repo` unique index. `repo` is
  // validated to the `owner/name` shape and the vocabulary columns to their
  // literal unions. NOTE: `env` is NON-SECRET by contract — it must carry only
  // non-sensitive values; secrets flow via `secretsRepo`. The handler logs the
  // written env key names on every write as an operator audit trail.
  Rpc.make("admin.orcaRepo.upsert", {
    payload: OrcaRepoUpsertInput,
    success: OrcaRepo.json,
    error: Schema.Union(Unauthorized, Forbidden, DatabaseError),
  }).middleware(AdminMiddleware),
  // Remove one row by its `owner/name` slug. A slug that matches no row yields
  // RecordNotFoundError.
  Rpc.make("admin.orcaRepo.delete", {
    payload: OrcaRepoRef,
    success: OrcaRepo.json,
    error: Schema.Union(Unauthorized, Forbidden, RecordNotFoundError, DatabaseError),
  }).middleware(AdminMiddleware),
  // Seen repos: the union of registered rows and every repo orca has built
  // against, for the Repositories list page. Read-only.
  Rpc.make("admin.orcaRepo.seen", {
    success: Schema.Array(OrcaRepoSeenRow),
    error: Schema.Union(Unauthorized, Forbidden, DatabaseError),
  }).middleware(AdminMiddleware),
  // The seven per-repo health checks as data. Probe failures are folded to
  // `unknown` checks — the RPC never fails on a flaky GitHub/gh/Linear call, so
  // the only declared errors are auth/DB.
  Rpc.make("admin.orcaRepo.preflight", {
    payload: OrcaRepoRef,
    success: OrcaRepoPreflight,
    error: Schema.Union(Unauthorized, Forbidden, DatabaseError),
  }).middleware(AdminMiddleware),
  // Per-field provenance of the repo's effective contract across in-repo →
  // registry → baked → default. GitHub read failures degrade to absent sources;
  // only the registry lookup can raise DatabaseError.
  Rpc.make("admin.orcaRepo.resolvedContract", {
    payload: OrcaRepoRef,
    success: OrcaRepoResolvedContract,
    error: Schema.Union(Unauthorized, Forbidden, DatabaseError),
  }).middleware(AdminMiddleware),
  // Read one of two allowlisted files (orca.json / package.json) from a repo for
  // the wizard's suggestions. A GitHub auth failure degrades to `text: null`
  // with a warning log, never a wire error.
  Rpc.make("admin.orcaRepo.file", {
    payload: OrcaRepoFileInput,
    success: OrcaRepoFile,
    error: Schema.Union(Unauthorized, Forbidden, DatabaseError),
  }).middleware(AdminMiddleware),
  // Onboard a repo: upsert the registry row and (optionally) ensure the
  // `repo:<slug>` Linear label exists on a team. Each step reports its own
  // ok/detail; a Linear failure never rolls back the upsert.
  Rpc.make("admin.orcaRepo.onboard", {
    payload: OrcaRepoOnboardInput,
    success: OrcaRepoOnboardResult,
    error: Schema.Union(Unauthorized, Forbidden, DatabaseError),
  }).middleware(AdminMiddleware),
) {}
